import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Anderson Campian',
    location: 'Madrid',
    rating: 5,
    text: 'Excelente comida peruana, los sabores son auténticos y el servicio es muy amable. El ceviche está espectacular, muy fresco. ¡Totalmente recomendado!',
    date: 'Hace 2 semanas',
  },
  {
    id: 2,
    name: 'Melisu Amenabar del Castillo',
    location: 'Madrid',
    rating: 5,
    text: 'Un lugar increíble para disfrutar de la gastronomía peruana. El tiradito de pescado es una delicia y el ambiente es muy acogedor. Volveré seguro.',
    date: 'Hace 1 mes',
  },
  {
    id: 3,
    name: 'Gladys Gaona',
    location: 'Entrevías',
    rating: 5,
    text: 'La mejor comida peruana que he probado en Madrid. Las conchitas a la chalaca están perfectas y el chicharrón de pescado crujiente y delicioso.',
    date: 'Hace 3 semanas',
  },
  {
    id: 4,
    name: 'Jorge Antonio Vásquez Saldaña',
    location: 'Madrid',
    rating: 5,
    text: 'Sabores que te transportan directamente al Perú. La fusión oriental le da un toque único. El trato es muy personalizado y familiar. ¡Imperdible!',
    date: 'Hace 1 semana',
  },
];

const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="testimonios"
      data-testid="testimonials-section"
      ref={ref}
      className="py-20 md:py-32 bg-cream-DEFAULT relative overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 text-terracotta/10">
        <Quote className="w-40 h-40" />
      </div>
      <div className="absolute bottom-10 right-10 text-gold/10">
        <Quote className="w-32 h-32 rotate-180" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-terracotta font-medium uppercase tracking-widest text-sm">
            Opiniones
          </span>
          <h2
            className="font-heading text-4xl md:text-5xl font-bold text-coffee mt-4 mb-6"
            data-testid="testimonials-title"
          >
            Lo que dicen nuestros clientes
          </h2>
          <div className="flex justify-center items-center gap-2 text-gold">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-current" />
            ))}
            <span className="text-coffee-light ml-2 font-medium">4.9/5 en Google</span>
          </div>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 relative"
              data-testid={`testimonial-${testimonial.id}`}
            >
              {/* Quote Icon */}
              <div className="absolute -top-4 -left-4 bg-terracotta p-3 rounded-full shadow-lg">
                <Quote className="w-5 h-5 text-white" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4 ml-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-gold fill-current" />
                ))}
              </div>

              {/* Text */}
              <p className="text-coffee-light text-base leading-relaxed mb-6 italic">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between border-t border-border/50 pt-4">
                <div>
                  <p className="font-semibold text-coffee">{testimonial.name}</p>
                  <p className="text-coffee-muted text-sm">{testimonial.location}</p>
                </div>
                <span className="text-coffee-muted text-sm">{testimonial.date}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Google Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-12"
        >
          <a
            href="https://g.page/salkantay-madrid/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            data-testid="google-reviews-link"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-coffee font-medium">Ver todas las opiniones en Google</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
